
import { createElement } from 'react'
import { Navigate } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'
import type { createStore } from 'jotai'
import { AppContainer } from './AppContainer'
import { pageLinks } from './pageLinks'
import { RouteErrorBoundary } from './components/RouteErrorBoundary'
import NotFoundPage from './pages/NotFoundPage'
import { LandingLayout } from './LandingLayout'
// == STYLE_PLAYGROUND_IMPORTS ==
import ArtisanCatalog from './pages/style-playground/alternatives/ArtisanCatalog'
import ObsidianCyber from './pages/style-playground/alternatives/ObsidianCyber'
import BlueprintWire from './pages/style-playground/alternatives/BlueprintWire'
import SoftElevation from './pages/style-playground/alternatives/SoftElevation'
import StudioBrut from './pages/style-playground/alternatives/StudioBrut'
import AuraGlass from './pages/style-playground/alternatives/AuraGlass'
import ArchitecturalGrid from './pages/style-playground/alternatives/ArchitecturalGrid'
import PristineBento from './pages/style-playground/alternatives/PristineBento'
// == PAGE_IMPORTS ==
import Dashboard from './pages/function/Dashboard'
import ComponentList from './pages/function/ComponentList'
import ComponentNew from './pages/function/ComponentNew'
import ComponentDetail from './pages/function/ComponentDetail'
import ComponentPreview from './pages/function/ComponentPreview'
import VersionHistory from './pages/function/VersionHistory'
import Changelog from './pages/function/Changelog'
import TokenOverview from './pages/function/TokenOverview'
import TokenCategoryDetail from './pages/function/TokenCategoryDetail'
// == STORE_IMPORTS ==
import * as DashboardStore from './stores/DashboardStore'
import * as ComponentListStore from './stores/ComponentListStore'
import * as ComponentNewStore from './stores/ComponentNewStore'
import * as ComponentDetailStore from './stores/ComponentDetailStore'
import * as VersionHistoryStore from './stores/VersionHistoryStore'
import * as ChangelogStore from './stores/ChangelogStore'
import * as TokenOverviewStore from './stores/TokenOverviewStore'
import * as TokenCategoryDetailStore from './stores/TokenCategoryDetailStore'


type Store = ReturnType<typeof createStore>

// ============================================================================
// ⚠️  CRITICAL: pageLinks ↔ createBusinessRoutes Contract
// ============================================================================
// 1. Define ALL page paths in `pageLinks.ts`
// 2. Use `pageLinks.xxx()` in `createBusinessRoutes` below (NOT hardcoded strings)
//
// This ensures:
// - Type-safe navigation throughout the app via `import { pageLinks } from './pageLinks'`
// - Single source of truth for all routes
// - Compile-time errors if routes are missing or mistyped
// ============================================================================

function createBusinessRoutes(store: Store): RouteObject[] {
  return [
    // == BUSINESS_ROUTES ==
    { path: pageLinks.Dashboard(), element: createElement(Dashboard), loader: async () => { DashboardStore.loader(store); return null } },
    { path: pageLinks.ComponentList(), element: createElement(ComponentList), loader: async () => { ComponentListStore.loader(store); return null } },
    { path: pageLinks.ComponentNew(), element: createElement(ComponentNew), loader: async () => { ComponentNewStore.loader(store); return null } },
    { path: pageLinks.ComponentDetail(':id'), element: createElement(ComponentDetail), loader: async ({ params }) => { ComponentDetailStore.loader(store, params); return null } },
    { path: pageLinks.ComponentPreview(':id'), element: createElement(ComponentPreview), loader: async ({ params }) => { ComponentDetailStore.loader(store, params); return null } },
    { path: pageLinks.VersionHistory(':id'), element: createElement(VersionHistory), loader: async ({ params }) => { VersionHistoryStore.loader(store, params); return null } },
    { path: pageLinks.Changelog(), element: createElement(Changelog), loader: async () => { ChangelogStore.loader(store); return null } },
    { path: pageLinks.TokenOverview(), element: createElement(TokenOverview), loader: async () => { TokenOverviewStore.loader(store); return null } },
    { path: pageLinks.TokenCategoryDetail(':category'), element: createElement(TokenCategoryDetail), loader: async ({ params }) => { TokenCategoryDetailStore.loader(store, params); return null } },
  ]
}

function createLandingRoutes(_store: Store): RouteObject[] {
  return [
    {
      element: createElement(LandingLayout),
      children: [
        // == LANDING_ROUTES ==
      ],
    },
  ]
}

function createPlaygroundRoutes(): RouteObject[] {
  return [
    // == STYLE_PLAYGROUND_ROUTES ==
    { path: '/style-playground/ArtisanCatalog', element: createElement(ArtisanCatalog) },
    { path: '/style-playground/ObsidianCyber', element: createElement(ObsidianCyber) },
    { path: '/style-playground/BlueprintWire', element: createElement(BlueprintWire) },
    { path: '/style-playground/SoftElevation', element: createElement(SoftElevation) },
    { path: '/style-playground/StudioBrut', element: createElement(StudioBrut) },
    { path: '/style-playground/AuraGlass', element: createElement(AuraGlass) },
    { path: '/style-playground/ArchitecturalGrid', element: createElement(ArchitecturalGrid) },
    { path: '/style-playground/PristineBento', element: createElement(PristineBento) },


  ]
}

// Fallback routes (must be last in children array)
function createFallbackRoutes(): RouteObject[] {
  const fallbackRoutes: RouteObject[] = []
  // 404 catch-all
  fallbackRoutes.push({
    path: '*',
    element: createElement(NotFoundPage),
  })
  return fallbackRoutes
}

export function createRoutes(store: Store): RouteObject[] {
  return [
    {
      path: '/',
      element: createElement(AppContainer),
      errorElement: createElement(RouteErrorBoundary),


      children: [
        ...createLandingRoutes(store),
        ...createPlaygroundRoutes(),
        ...createBusinessRoutes(store),


        ...createFallbackRoutes(),
      ],
    },
  ]
}
